import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, Play, Star, Calendar, Clock, Film, Tv, Heart, X, ChevronLeft, ChevronRight, ExternalLink, Loader2, Menu, TrendingUp, Filter, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Language options matching the ecosystem
const LANGUAGES = [
  { code: 'en', label: 'English', flag: 'GB' },
  { code: 'es', label: 'Español', flag: 'ES' },
  { code: 'pt', label: 'Português', flag: 'PT' },
  { code: 'fr', label: 'Français', flag: 'FR' },
  { code: 'de', label: 'Deutsch', flag: 'DE' },
  { code: 'it', label: 'Italiano', flag: 'IT' },
];

// Translations for the UI
const TRANSLATIONS = {
  en: {
    heroTitle: "Your Personal Guide to",
    heroHighlight: "What's Streaming",
    heroSubtitle: "Find where movies and TV shows are streaming – legally – across all your services.",
    streamingServices: "100+ Streaming Services",
    regionAware: "Region-Aware Results",
    personalWatchlist: "Personal Watchlist",
    trendingMovies: "Trending Movies This Week",
    trendingTV: "Trending TV Shows This Week",
    searchPlaceholder: "Search movies & TV shows...",
    home: "Home",
    discover: "Discover",
    watchlist: "Watchlist",
    myWatchlist: "My Watchlist",
    searchResults: "Search Results for",
    whereToWatch: "Where to Watch",
    overview: "Overview",
    trailer: "Trailer",
    cast: "Cast",
    stream: "Stream",
    freeWithAds: "Free with Ads",
    rent: "Rent",
    buy: "Buy",
    addToWatchlist: "Add to Watchlist",
    inWatchlist: "In Watchlist",
    noResults: "No results found for",
    tryDifferent: "Try a different search term",
    emptyWatchlist: "Your watchlist is empty",
    startAdding: "Start adding movies and TV shows to keep track of what you want to watch",
    loadMore: "Load More",
    noStreaming: "No streaming information available for your region.",
    tryChangingRegion: "Try changing your region in settings.",
    streamingData: "Streaming availability data provided by JustWatch via TMDB",
    footerDesc: "Your personal guide to what's streaming. Find where movies and TV shows are available legally across all your favorite services.",
    ecosystem: "The Movie Matrix Ecosystem",
    about: "About",
    noHost: "We do not host any content",
    officialProviders: "All links direct to official providers",
    copyright: "My Matrix Streams. Part of The Movie Matrix Ecosystem.",
    movies: "Movies",
    tvShows: "TV Shows",
    allGenres: "All Genres",
    mostPopular: "Most Popular",
    topRated: "Top Rated",
    newest: "Newest",
    highestRevenue: "Highest Revenue",
    all: "All",
    watchOnYouTube: "Watch on YouTube",
    votes: "votes",
    season: "Season",
    seasons: "Seasons",
    removedFromWatchlist: "Removed from watchlist",
    addedToWatchlist: "Added to watchlist",
  },
  es: {
    heroTitle: "Tu Guía Personal de",
    heroHighlight: "Lo Que Está en Streaming",
    heroSubtitle: "Encuentra dónde ver películas y series – legalmente – en todos tus servicios.",
    streamingServices: "100+ Servicios de Streaming",
    regionAware: "Resultados por Región",
    personalWatchlist: "Lista Personal",
    trendingMovies: "Películas Tendencia Esta Semana",
    trendingTV: "Series Tendencia Esta Semana",
    searchPlaceholder: "Buscar películas y series...",
    home: "Inicio",
    discover: "Descubrir",
    watchlist: "Mi Lista",
    myWatchlist: "Mi Lista",
    searchResults: "Resultados de búsqueda para",
    whereToWatch: "Dónde Ver",
    overview: "Sinopsis",
    trailer: "Tráiler",
    cast: "Reparto",
    stream: "Streaming",
    freeWithAds: "Gratis con Anuncios",
    rent: "Alquilar",
    buy: "Comprar",
    addToWatchlist: "Añadir a Mi Lista",
    inWatchlist: "En Mi Lista",
    noResults: "No se encontraron resultados para",
    tryDifferent: "Intenta con otro término de búsqueda",
    emptyWatchlist: "Tu lista está vacía",
    startAdding: "Empieza a añadir películas y series para recordar lo que quieres ver",
    loadMore: "Cargar Más",
    noStreaming: "No hay información de streaming disponible para tu región.",
    tryChangingRegion: "Intenta cambiar tu región en ajustes.",
    streamingData: "Datos de streaming proporcionados por JustWatch vía TMDB",
    footerDesc: "Tu guía personal de lo que está en streaming. Encuentra dónde ver películas y series legalmente en todos tus servicios favoritos.",
    ecosystem: "El Ecosistema Movie Matrix",
    about: "Acerca de",
    noHost: "No alojamos ningún contenido",
    officialProviders: "Todos los enlaces dirigen a proveedores oficiales",
    copyright: "My Matrix Streams. Parte del Ecosistema Movie Matrix.",
    movies: "Películas",
    tvShows: "Series",
    allGenres: "Todos los Géneros",
    mostPopular: "Más Popular",
    topRated: "Mejor Valoradas",
    newest: "Más Recientes",
    highestRevenue: "Mayor Recaudación",
    all: "Todos",
    watchOnYouTube: "Ver en YouTube",
    votes: "votos",
    season: "Temporada",
    seasons: "Temporadas",
    removedFromWatchlist: "Eliminado de tu lista",
    addedToWatchlist: "Añadido a tu lista",
  },
  pt: {
    heroTitle: "Seu Guia Pessoal do",
    heroHighlight: "Que Está em Streaming",
    heroSubtitle: "Encontre onde assistir filmes e séries – legalmente – em todos os seus serviços.",
    streamingServices: "100+ Serviços de Streaming",
    regionAware: "Resultados por Região",
    personalWatchlist: "Lista Pessoal",
    trendingMovies: "Filmes em Alta Esta Semana",
    trendingTV: "Séries em Alta Esta Semana",
    searchPlaceholder: "Buscar filmes e séries...",
    home: "Início",
    discover: "Descobrir",
    watchlist: "Minha Lista",
    myWatchlist: "Minha Lista",
    searchResults: "Resultados da busca para",
    whereToWatch: "Onde Assistir",
    overview: "Sinopse",
    trailer: "Trailer",
    cast: "Elenco",
    stream: "Streaming",
    freeWithAds: "Grátis com Anúncios",
    rent: "Alugar",
    buy: "Comprar",
    addToWatchlist: "Adicionar à Lista",
    inWatchlist: "Na Minha Lista",
    noResults: "Nenhum resultado encontrado para",
    tryDifferent: "Tente um termo de busca diferente",
    emptyWatchlist: "Sua lista está vazia",
    startAdding: "Comece a adicionar filmes e séries para lembrar o que você quer assistir",
    loadMore: "Carregar Mais",
    noStreaming: "Nenhuma informação de streaming disponível para sua região.",
    tryChangingRegion: "Tente mudar sua região nas configurações.",
    streamingData: "Dados de streaming fornecidos por JustWatch via TMDB",
    footerDesc: "Seu guia pessoal do que está em streaming. Encontre onde assistir filmes e séries legalmente em todos os seus serviços favoritos.",
    ecosystem: "O Ecossistema Movie Matrix",
    about: "Sobre",
    noHost: "Não hospedamos nenhum conteúdo",
    officialProviders: "Todos os links direcionam para provedores oficiais",
    copyright: "My Matrix Streams. Parte do Ecossistema Movie Matrix.",
    movies: "Filmes",
    tvShows: "Séries",
    allGenres: "Todos os Gêneros",
    mostPopular: "Mais Popular",
    topRated: "Mais Bem Avaliados",
    newest: "Mais Recentes",
    highestRevenue: "Maior Bilheteria",
    all: "Todos",
    watchOnYouTube: "Assistir no YouTube",
    votes: "votos",
    season: "Temporada",
    seasons: "Temporadas",
    removedFromWatchlist: "Removido da sua lista",
    addedToWatchlist: "Adicionado à sua lista",
  },
  fr: {
    heroTitle: "Votre Guide Personnel de",
    heroHighlight: "Ce Qui Est en Streaming",
    heroSubtitle: "Trouvez où regarder films et séries – légalement – sur tous vos services.",
    streamingServices: "100+ Services de Streaming",
    regionAware: "Résultats par Région",
    personalWatchlist: "Liste Personnelle",
    trendingMovies: "Films Tendance Cette Semaine",
    trendingTV: "Séries Tendance Cette Semaine",
    searchPlaceholder: "Rechercher films et séries...",
    home: "Accueil",
    discover: "Découvrir",
    watchlist: "Ma Liste",
    myWatchlist: "Ma Liste",
    searchResults: "Résultats de recherche pour",
    whereToWatch: "Où Regarder",
    overview: "Synopsis",
    trailer: "Bande-annonce",
    cast: "Distribution",
    stream: "Streaming",
    freeWithAds: "Gratuit avec Pubs",
    rent: "Louer",
    buy: "Acheter",
    addToWatchlist: "Ajouter à Ma Liste",
    inWatchlist: "Dans Ma Liste",
    noResults: "Aucun résultat trouvé pour",
    tryDifferent: "Essayez un autre terme de recherche",
    emptyWatchlist: "Votre liste est vide",
    startAdding: "Commencez à ajouter des films et séries pour vous souvenir de ce que vous voulez regarder",
    loadMore: "Charger Plus",
    noStreaming: "Aucune information de streaming disponible pour votre région.",
    tryChangingRegion: "Essayez de changer votre région dans les paramètres.",
    streamingData: "Données de streaming fournies par JustWatch via TMDB",
    footerDesc: "Votre guide personnel de ce qui est en streaming. Trouvez où regarder films et séries légalement sur tous vos services préférés.",
    ecosystem: "L'Écosystème Movie Matrix",
    about: "À Propos",
    noHost: "Nous n'hébergeons aucun contenu",
    officialProviders: "Tous les liens mènent aux fournisseurs officiels",
    copyright: "My Matrix Streams. Partie de l'Écosystème Movie Matrix.",
    movies: "Films",
    tvShows: "Séries",
    allGenres: "Tous les Genres",
    mostPopular: "Plus Populaire",
    topRated: "Mieux Notés",
    newest: "Plus Récents",
    highestRevenue: "Plus Gros Revenus",
    all: "Tous",
    watchOnYouTube: "Regarder sur YouTube",
    votes: "votes",
    season: "Saison",
    seasons: "Saisons",
    removedFromWatchlist: "Retiré de votre liste",
    addedToWatchlist: "Ajouté à votre liste",
  },
  de: {
    heroTitle: "Dein Persönlicher Guide zu",
    heroHighlight: "Was Gerade Streamt",
    heroSubtitle: "Finde, wo Filme und Serien streamen – legal – auf all deinen Diensten.",
    streamingServices: "100+ Streaming-Dienste",
    regionAware: "Regionsbezogene Ergebnisse",
    personalWatchlist: "Persönliche Watchlist",
    trendingMovies: "Trendfilme Diese Woche",
    trendingTV: "Trendserien Diese Woche",
    searchPlaceholder: "Filme und Serien suchen...",
    home: "Startseite",
    discover: "Entdecken",
    watchlist: "Watchlist",
    myWatchlist: "Meine Watchlist",
    searchResults: "Suchergebnisse für",
    whereToWatch: "Wo Anschauen",
    overview: "Übersicht",
    trailer: "Trailer",
    cast: "Besetzung",
    stream: "Streaming",
    freeWithAds: "Kostenlos mit Werbung",
    rent: "Leihen",
    buy: "Kaufen",
    addToWatchlist: "Zur Watchlist",
    inWatchlist: "In der Watchlist",
    noResults: "Keine Ergebnisse gefunden für",
    tryDifferent: "Versuche einen anderen Suchbegriff",
    emptyWatchlist: "Deine Watchlist ist leer",
    startAdding: "Füge Filme und Serien hinzu, um den Überblick zu behalten",
    loadMore: "Mehr Laden",
    noStreaming: "Keine Streaming-Informationen für deine Region verfügbar.",
    tryChangingRegion: "Versuche, deine Region in den Einstellungen zu ändern.",
    streamingData: "Streaming-Daten bereitgestellt von JustWatch über TMDB",
    footerDesc: "Dein persönlicher Guide zu dem, was gerade streamt. Finde, wo Filme und Serien legal auf all deinen Lieblingsdiensten verfügbar sind.",
    ecosystem: "Das Movie Matrix Ökosystem",
    about: "Über Uns",
    noHost: "Wir hosten keine Inhalte",
    officialProviders: "Alle Links führen zu offiziellen Anbietern",
    copyright: "My Matrix Streams. Teil des Movie Matrix Ökosystems.",
    movies: "Filme",
    tvShows: "Serien",
    allGenres: "Alle Genres",
    mostPopular: "Beliebteste",
    topRated: "Bestbewertet",
    newest: "Neueste",
    highestRevenue: "Höchste Einnahmen",
    all: "Alle",
    watchOnYouTube: "Auf YouTube ansehen",
    votes: "Stimmen",
    season: "Staffel",
    seasons: "Staffeln",
    removedFromWatchlist: "Von der Watchlist entfernt",
    addedToWatchlist: "Zur Watchlist hinzugefügt",
  },
  it: {
    heroTitle: "La Tua Guida Personale a",
    heroHighlight: "Cosa È in Streaming",
    heroSubtitle: "Trova dove guardare film e serie TV – legalmente – su tutti i tuoi servizi.",
    streamingServices: "100+ Servizi di Streaming",
    regionAware: "Risultati per Regione",
    personalWatchlist: "Lista Personale",
    trendingMovies: "Film di Tendenza Questa Settimana",
    trendingTV: "Serie TV di Tendenza Questa Settimana",
    searchPlaceholder: "Cerca film e serie TV...",
    home: "Home",
    discover: "Scopri",
    watchlist: "La Mia Lista",
    myWatchlist: "La Mia Lista",
    searchResults: "Risultati di ricerca per",
    whereToWatch: "Dove Guardare",
    overview: "Trama",
    trailer: "Trailer",
    cast: "Cast",
    stream: "Streaming",
    freeWithAds: "Gratis con Pubblicità",
    rent: "Noleggia",
    buy: "Acquista",
    addToWatchlist: "Aggiungi alla Lista",
    inWatchlist: "Nella Mia Lista",
    noResults: "Nessun risultato trovato per",
    tryDifferent: "Prova un altro termine di ricerca",
    emptyWatchlist: "La tua lista è vuota",
    startAdding: "Inizia ad aggiungere film e serie TV per tenere traccia di cosa vuoi guardare",
    loadMore: "Carica Altri",
    noStreaming: "Nessuna informazione di streaming disponibile per la tua regione.",
    tryChangingRegion: "Prova a cambiare la tua regione nelle impostazioni.",
    streamingData: "Dati di streaming forniti da JustWatch tramite TMDB",
    footerDesc: "La tua guida personale a cosa è in streaming. Trova dove guardare film e serie TV legalmente su tutti i tuoi servizi preferiti.",
    ecosystem: "L'Ecosistema Movie Matrix",
    about: "Info",
    noHost: "Non ospitiamo alcun contenuto",
    officialProviders: "Tutti i link portano a fornitori ufficiali",
    copyright: "My Matrix Streams. Parte dell'Ecosistema Movie Matrix.",
    movies: "Film",
    tvShows: "Serie TV",
    allGenres: "Tutti i Generi",
    mostPopular: "Più Popolari",
    topRated: "Più Votati",
    newest: "Più Recenti",
    highestRevenue: "Maggiori Incassi",
    all: "Tutti",
    watchOnYouTube: "Guarda su YouTube",
    votes: "voti",
    season: "Stagione",
    seasons: "Stagioni",
    removedFromWatchlist: "Rimosso dalla tua lista",
    addedToWatchlist: "Aggiunto alla tua lista",
  },
};

// Get translation helper
const useTranslation = (language) => {
  return TRANSLATIONS[language] || TRANSLATIONS.en;
};

// Logo component using the provided logo URL
const Logo = ({ className = "", size = "default" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <img 
      src="https://customer-assets.emergentagent.com/job_afba84cf-9489-4aaa-8f83-b4922d1c3e76/artifacts/px5bvfi3_ChatGPT%20Image%20Jan%2017%2C%202026%2C%2008_45_42%20PM.png"
      alt="My Matrix Streams"
      className={size === "large" ? "h-20 w-auto" : "h-14 w-auto"}
    />
  </div>
);

// Watchlist utilities (localStorage based)
const getWatchlist = () => {
  try {
    return JSON.parse(localStorage.getItem('mymatrix_watchlist') || '[]');
  } catch {
    return [];
  }
};

const addToWatchlist = (item) => {
  const watchlist = getWatchlist();
  const exists = watchlist.some(w => w.id === item.id && w.media_type === item.media_type);
  if (!exists) {
    watchlist.push({
      id: item.id,
      media_type: item.media_type,
      title: item.title || item.name,
      poster_url: item.poster_url,
      vote_average: item.vote_average,
      release_date: item.release_date || item.first_air_date,
      added_at: new Date().toISOString()
    });
    localStorage.setItem('mymatrix_watchlist', JSON.stringify(watchlist));
    return true;
  }
  return false;
};

const removeFromWatchlist = (id, media_type) => {
  const watchlist = getWatchlist();
  const filtered = watchlist.filter(w => !(w.id === id && w.media_type === media_type));
  localStorage.setItem('mymatrix_watchlist', JSON.stringify(filtered));
};

const isInWatchlist = (id, media_type) => {
  const watchlist = getWatchlist();
  return watchlist.some(w => w.id === id && w.media_type === media_type);
};

// Content Card Component
const ContentCard = ({ item, onClick, onWatchlistToggle }) => {
  const [inWatchlist, setInWatchlist] = useState(false);
  
  useEffect(() => {
    setInWatchlist(isInWatchlist(item.id, item.media_type));
  }, [item.id, item.media_type]);
  
  const handleWatchlistClick = (e) => {
    e.stopPropagation();
    if (inWatchlist) {
      removeFromWatchlist(item.id, item.media_type);
      setInWatchlist(false);
      toast.success('Removed from watchlist');
    } else {
      addToWatchlist(item);
      setInWatchlist(true);
      toast.success('Added to watchlist');
    }
    if (onWatchlistToggle) onWatchlistToggle();
  };
  
  return (
    <Card 
      data-testid={`content-card-${item.id}`}
      className="group cursor-pointer bg-black/40 border-matrix-green/20 hover:border-matrix-green/60 transition-all duration-300 overflow-hidden"
      onClick={() => onClick(item)}
    >
      <div className="relative aspect-[2/3] overflow-hidden">
        {item.poster_url ? (
          <img 
            src={item.poster_url} 
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-matrix-dark flex items-center justify-center">
            <Film className="w-12 h-12 text-matrix-green/30" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <Button
          data-testid={`watchlist-btn-${item.id}`}
          variant="ghost"
          size="icon"
          className={`absolute top-2 right-2 ${inWatchlist ? 'text-matrix-green bg-black/60' : 'text-white bg-black/40'} hover:bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity`}
          onClick={handleWatchlistClick}
        >
          <Heart className={`w-5 h-5 ${inWatchlist ? 'fill-current' : ''}`} />
        </Button>
        <Badge className="absolute top-2 left-2 bg-matrix-green/90 text-black text-xs">
          {item.media_type === 'movie' ? 'Movie' : 'TV'}
        </Badge>
        {item.vote_average > 0 && (
          <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/70 px-2 py-1 rounded-full text-xs">
            <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
            <span className="text-white">{item.vote_average.toFixed(1)}</span>
          </div>
        )}
      </div>
      <CardContent className="p-3">
        <h3 className="font-semibold text-white text-sm line-clamp-2 group-hover:text-matrix-green transition-colors">
          {item.title || item.name}
        </h3>
        {(item.release_date || item.first_air_date) && (
          <p className="text-matrix-green/60 text-xs mt-1">
            {new Date(item.release_date || item.first_air_date).getFullYear()}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

// Content Row Component
const ContentRow = ({ title, items, loading, onItemClick, icon: Icon }) => (
  <div className="mb-8">
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-5 h-5 text-matrix-green" />}
      <h2 className="text-xl font-bold text-white">{title}</h2>
    </div>
    <ScrollArea className="w-full whitespace-nowrap">
      <div className="flex gap-4 pb-4">
        {loading ? (
          [...Array(6)].map((_, i) => (
            <div key={i} className="w-[160px] flex-shrink-0">
              <Skeleton className="aspect-[2/3] bg-matrix-green/10" />
              <Skeleton className="h-4 mt-2 bg-matrix-green/10" />
            </div>
          ))
        ) : (
          items.map((item) => (
            <div key={`${item.media_type}-${item.id}`} className="w-[160px] flex-shrink-0">
              <ContentCard item={item} onClick={onItemClick} />
            </div>
          ))
        )}
      </div>
      <ScrollBar orientation="horizontal" className="bg-matrix-green/20" />
    </ScrollArea>
  </div>
);

// Provider Badge Component - Now clickable with direct links to streaming services
const ProviderBadge = ({ provider, type, mediaType, contentId, contentTitle }) => {
  // Your YouTube Channel URL
  const YOUR_YOUTUBE_CHANNEL = 'https://www.youtube.com/@TheMovieMatrixTV';
  
  // Direct streaming service URLs mapping by provider_id
  const getDirectProviderLink = () => {
    const searchQuery = encodeURIComponent(contentTitle || '');
    const providerId = provider.provider_id;
    const providerName = provider.provider_name?.toLowerCase() || '';
    
    // Check if this is a YouTube provider - link to your channel
    if (providerName.includes('youtube')) {
      return YOUR_YOUTUBE_CHANNEL;
    }
    
    // Map of provider IDs to their direct search/browse URLs
    const providerUrls = {
      // Netflix (all variants)
      8: `https://www.netflix.com/search?q=${searchQuery}`,
      175: `https://www.netflix.com/search?q=${searchQuery}`,
      // Amazon Prime Video (all variants including blue and white icons)
      9: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      10: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      119: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      1796: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      1797: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      1798: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      1824: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      1825: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      // Disney+
      337: `https://www.disneyplus.com/search?q=${searchQuery}`,
      390: `https://www.disneyplus.com/search?q=${searchQuery}`,
      // Hulu
      15: `https://www.hulu.com/search?q=${searchQuery}`,
      // Max (formerly HBO Max)
      384: `https://play.max.com/search?q=${searchQuery}`,
      1899: `https://play.max.com/search?q=${searchQuery}`,
      // HBO Max Amazon Channel
      582: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      // Apple TV+
      350: `https://tv.apple.com/search?term=${searchQuery}`,
      2: `https://tv.apple.com/search?term=${searchQuery}`,
      // Paramount+
      531: `https://www.paramountplus.com/search/?q=${searchQuery}`,
      582: `https://www.paramountplus.com/search/?q=${searchQuery}`,
      // Peacock
      386: `https://www.peacocktv.com/search?q=${searchQuery}`,
      387: `https://www.peacocktv.com/search?q=${searchQuery}`,
      // YouTube - Link to your channel
      192: YOUR_YOUTUBE_CHANNEL,
      188: YOUR_YOUTUBE_CHANNEL,
      235: YOUR_YOUTUBE_CHANNEL,
      // Google Play Movies
      3: `https://play.google.com/store/search?q=${searchQuery}&c=movies`,
      // Vudu / Fandango at Home
      7: `https://www.vudu.com/content/movies/search?searchString=${searchQuery}`,
      // Crunchyroll
      283: `https://www.crunchyroll.com/search?q=${searchQuery}`,
      // Tubi
      73: `https://tubitv.com/search/${searchQuery}`,
      // Pluto TV
      300: `https://pluto.tv/search/details/${searchQuery}`,
      // Freevee (Amazon)
      613: `https://www.primevideo.com/search?phrase=${searchQuery}`,
      // Starz
      43: `https://www.starz.com/search?q=${searchQuery}`,
      // Showtime
      37: `https://www.sho.com/search?q=${searchQuery}`,
      // MGM+
      636: `https://www.mgmplus.com/search?q=${searchQuery}`,
      // BritBox
      151: `https://www.britbox.com/us/search?q=${searchQuery}`,
      // Criterion Channel
      258: `https://www.criterionchannel.com/search?q=${searchQuery}`,
      // Mubi
      11: `https://mubi.com/en/search?query=${searchQuery}`,
      // Shudder
        99: `https://www.shudder.com/search?q=${searchQuery}`,
      // AMC+
      526: `https://www.amcplus.com/search?q=${searchQuery}`,
      // Discovery+
      520: `https://www.discoveryplus.com/search?q=${searchQuery}`,
      // ESPN+
      899: `https://www.espn.com/search/_/q/${searchQuery}`,
      // Roku Channel
      207: `https://therokuchannel.roku.com/search?q=${searchQuery}`,
      // Plex
      538: `https://watch.plex.tv/search?q=${searchQuery}`,
      // TBS
      506: `https://www.tbs.com/search?q=${searchQuery}`,
      // TNT
      363: `https://www.tntdrama.com/search?q=${searchQuery}`,
      // FX Now
      123: `https://www.fxnetworks.com/search?q=${searchQuery}`,
      // USA Network
      322: `https://www.usanetwork.com/search?q=${searchQuery}`,
      // Syfy
      215: `https://www.syfy.com/search?q=${searchQuery}`,
      // A&E
      397: `https://www.aetv.com/search?q=${searchQuery}`,
      // History
      155: `https://www.history.com/search?q=${searchQuery}`,
      // Lifetime
      157: `https://www.mylifetime.com/search?q=${searchQuery}`,
    };
    
    // Return direct URL if we have a mapping
    if (providerUrls[providerId]) {
      return providerUrls[providerId];
    }
    
    // Fallback: Try to match based on provider name
    const normalizedName = providerName.replace(/[^a-z0-9]/g, '');
    
    // Name-based fallbacks
    if (normalizedName.includes('netflix')) {
      return `https://www.netflix.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('prime') || normalizedName.includes('amazon')) {
      return `https://www.primevideo.com/search?phrase=${searchQuery}`;
    }
    if (normalizedName.includes('disney')) {
      return `https://www.disneyplus.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('hulu')) {
      return `https://www.hulu.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('hbo') || normalizedName.includes('max')) {
      return `https://play.max.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('apple')) {
      return `https://tv.apple.com/search?term=${searchQuery}`;
    }
    if (normalizedName.includes('paramount')) {
      return `https://www.paramountplus.com/search/?q=${searchQuery}`;
    }
    if (normalizedName.includes('peacock')) {
      return `https://www.peacocktv.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('youtube')) {
      return YOUR_YOUTUBE_CHANNEL;
    }
    if (normalizedName.includes('tbs')) {
      return `https://www.tbs.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('tnt')) {
      return `https://www.tntdrama.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('tubi')) {
      return `https://tubitv.com/search/${searchQuery}`;
    }
    if (normalizedName.includes('pluto')) {
      return `https://pluto.tv/search/details/${searchQuery}`;
    }
    if (normalizedName.includes('vudu') || normalizedName.includes('fandango')) {
      return `https://www.vudu.com/content/movies/search?searchString=${searchQuery}`;
    }
    if (normalizedName.includes('starz')) {
      return `https://www.starz.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('showtime')) {
      return `https://www.sho.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('crunchyroll')) {
      return `https://www.crunchyroll.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('roku')) {
      return `https://therokuchannel.roku.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('discovery')) {
      return `https://www.discoveryplus.com/search?q=${searchQuery}`;
    }
    if (normalizedName.includes('amc')) {
      return `https://www.amcplus.com/search?q=${searchQuery}`;
    }
    
    // Last resort: Direct to streaming service homepage (better than Google)
    // Try to extract a reasonable domain from the provider name
    const cleanName = provider.provider_name?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    if (cleanName) {
      return `https://www.${cleanName}.com`;
    }
    
    // Absolute last resort
    return `https://www.google.com/search?q=${searchQuery}+watch+on+${encodeURIComponent(provider.provider_name)}`;
  };
  
  return (
    <a 
      href={getDirectProviderLink()}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col items-center gap-1 group cursor-pointer hover:scale-105 transition-transform"
      title={`Watch on ${provider.provider_name}`}
    >
      <div className="relative">
        {provider.logo_url ? (
          <img 
            src={provider.logo_url} 
            alt={provider.provider_name}
            className="w-12 h-12 rounded-lg object-cover bg-white group-hover:ring-2 group-hover:ring-matrix-green transition-all"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-matrix-green/20 flex items-center justify-center group-hover:ring-2 group-hover:ring-matrix-green">
            <Play className="w-6 h-6 text-matrix-green" />
          </div>
        )}
        <Badge 
          className={`absolute -bottom-1 -right-1 text-[10px] px-1 py-0 ${
            type === 'flatrate' ? 'bg-matrix-green text-black' :
            type === 'ads' ? 'bg-blue-500 text-white' :
            type === 'rent' ? 'bg-yellow-500 text-black' :
            'bg-purple-500 text-white'
          }`}
        >
          {type === 'flatrate' ? 'SUB' : type === 'ads' ? 'FREE' : type === 'rent' ? 'RENT' : 'BUY'}
        </Badge>
      </div>
      <span className="text-xs text-gray-400 text-center line-clamp-1 w-14 group-hover:text-matrix-green transition-colors">
        {provider.provider_name}
      </span>
    </a>
  );
};

// Content Detail Modal
const ContentDetailModal = ({ item, open, onClose, t }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [inWatchlist, setInWatchlist] = useState(false);
  
  useEffect(() => {
    if (item && open) {
      setLoading(true);
      setInWatchlist(isInWatchlist(item.id, item.media_type));
      
      axios.get(`${API}/${item.media_type}/${item.id}`)
        .then(res => setDetails(res.data))
        .catch(err => {
          console.error(err);
          toast.error('Failed to load details');
        })
        .finally(() => setLoading(false));
    }
  }, [item, open]);
  
  const handleWatchlistToggle = () => {
    if (inWatchlist) {
      removeFromWatchlist(item.id, item.media_type);
      setInWatchlist(false);
      toast.success(t?.removedFromWatchlist || 'Removed from watchlist');
    } else {
      addToWatchlist({ ...item, ...details });
      setInWatchlist(true);
      toast.success(t?.addedToWatchlist || 'Added to watchlist');
    }
  };
  
  const watchProviders = details?.watch_providers || {};
  const hasProviders = watchProviders.flatrate?.length || watchProviders.rent?.length || 
                       watchProviders.buy?.length || watchProviders.ads?.length;
  
  const trailer = details?.videos?.find(v => v.type === 'Trailer') || details?.videos?.[0];
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-matrix-dark border-matrix-green/30 text-white p-0 overflow-hidden max-h-[90vh]">
        <DialogTitle className="sr-only">{details?.title || item?.title || 'Content Details'}</DialogTitle>
        <ScrollArea className="max-h-[90vh]">
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-matrix-green" />
            </div>
          ) : details ? (
            <div>
              {/* Backdrop */}
              <div className="relative h-64 overflow-hidden">
                {details.backdrop_url ? (
                  <img 
                    src={details.backdrop_url} 
                    alt={details.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-matrix-green/20 to-black" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-matrix-dark via-matrix-dark/50 to-transparent" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 bg-black/50 hover:bg-black/80"
                  onClick={onClose}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Content */}
              <div className="p-6 -mt-20 relative z-10">
                <div className="flex gap-6">
                  {/* Poster */}
                  <div className="flex-shrink-0 hidden sm:block">
                    {details.poster_url ? (
                      <img 
                        src={details.poster_url} 
                        alt={details.title}
                        className="w-32 h-48 object-cover rounded-lg shadow-lg border border-matrix-green/30"
                      />
                    ) : (
                      <div className="w-32 h-48 bg-matrix-green/10 rounded-lg flex items-center justify-center">
                        <Film className="w-12 h-12 text-matrix-green/30" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1">
                    <div>
                      <h2 className="text-2xl font-bold text-white">{details.title}</h2>
                      {details.tagline && (
                        <p className="text-matrix-green/80 italic mt-1">"{details.tagline}"</p>
                      )}
                    </div>
                    
                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-gray-400">
                      {details.vote_average > 0 && (
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-white">{details.vote_average.toFixed(1)}</span>
                          <span>({details.vote_count} {t?.votes || 'votes'})</span>
                        </div>
                      )}
                      {(details.release_date || details.first_air_date) && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(details.release_date || details.first_air_date).toLocaleDateString()}</span>
                        </div>
                      )}
                      {details.runtime && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{Math.floor(details.runtime / 60)}h {details.runtime % 60}m</span>
                        </div>
                      )}
                      {details.number_of_seasons && (
                        <div className="flex items-center gap-1">
                          <Tv className="w-4 h-4" />
                          <span>{details.number_of_seasons} {details.number_of_seasons > 1 ? (t?.seasons || 'Seasons') : (t?.season || 'Season')}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Genres */}
                    {details.genres?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {details.genres.map(genre => (
                          <Badge key={genre.id} variant="outline" className="border-matrix-green/40 text-matrix-green">
                            {genre.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Watchlist Button - Now below genres for full visibility */}
                    <div className="mt-4">
                      <Button
                        data-testid="modal-watchlist-btn"
                        variant={inWatchlist ? "default" : "outline"}
                        className={inWatchlist ? "bg-matrix-green text-black hover:bg-matrix-green/90" : "border-matrix-green text-matrix-green hover:bg-matrix-green/10"}
                        onClick={handleWatchlistToggle}
                      >
                        <Heart className={`w-4 h-4 mr-2 ${inWatchlist ? 'fill-current' : ''}`} />
                        {inWatchlist ? (t?.inWatchlist || 'In Watchlist') : (t?.addToWatchlist || 'Add to Watchlist')}
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Overview */}
                {details.overview && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-matrix-green mb-2">{t?.overview || 'Overview'}</h3>
                    <p className="text-gray-300 leading-relaxed">{details.overview}</p>
                  </div>
                )}
                
                {/* Where to Watch */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-matrix-green mb-3 flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    {t?.whereToWatch || 'Where to Watch'}
                  </h3>
                  {hasProviders ? (
                    <div className="space-y-4">
                      {watchProviders.flatrate?.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-400 mb-2">{t?.stream || 'Stream'}</p>
                          <div className="flex flex-wrap gap-3">
                            {watchProviders.flatrate.map(p => (
                              <ProviderBadge key={p.provider_id} provider={p} type="flatrate" contentTitle={details.title} />
                            ))}
                          </div>
                        </div>
                      )}
                      {watchProviders.ads?.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-400 mb-2">{t?.freeWithAds || 'Free with Ads'}</p>
                          <div className="flex flex-wrap gap-3">
                            {watchProviders.ads.map(p => (
                              <ProviderBadge key={p.provider_id} provider={p} type="ads" contentTitle={details.title} />
                            ))}
                          </div>
                        </div>
                      )}
                      {watchProviders.rent?.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-400 mb-2">{t?.rent || 'Rent'}</p>
                          <div className="flex flex-wrap gap-3">
                            {watchProviders.rent.map(p => (
                              <ProviderBadge key={p.provider_id} provider={p} type="rent" contentTitle={details.title} />
                            ))}
                          </div>
                        </div>
                      )}
                      {watchProviders.buy?.length > 0 && (
                        <div>
                          <p className="text-sm text-gray-400 mb-2">{t?.buy || 'Buy'}</p>
                          <div className="flex flex-wrap gap-3">
                            {watchProviders.buy.map(p => (
                              <ProviderBadge key={p.provider_id} provider={p} type="buy" contentTitle={details.title} />
                            ))}
                          </div>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {t?.streamingData || 'Streaming availability data provided by JustWatch via TMDB'}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-matrix-green/5 border border-matrix-green/20 rounded-lg p-4 text-center">
                      <p className="text-gray-400">{t?.noStreaming || 'No streaming information available for your region.'}</p>
                      <p className="text-sm text-gray-500 mt-1">{t?.tryChangingRegion || 'Try changing your region in settings.'}</p>
                    </div>
                  )}
                </div>
                
                {/* Trailer */}
                {trailer && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-matrix-green mb-3">{t?.trailer || 'Trailer'}</h3>
                    <a 
                      href={`https://www.youtube.com/watch?v=${trailer.key}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-matrix-green hover:text-matrix-green-light transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t?.watchOnYouTube || 'Watch on YouTube'}
                    </a>
                  </div>
                )}
                
                {/* Cast */}
                {details.credits?.cast?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-matrix-green mb-3">{t?.cast || 'Cast'}</h3>
                    <ScrollArea className="w-full whitespace-nowrap">
                      <div className="flex gap-3 pb-2">
                        {details.credits.cast.slice(0, 10).map(person => (
                          <div key={person.id} className="flex-shrink-0 text-center w-20">
                            {person.profile_path ? (
                              <img 
                                src={`https://image.tmdb.org/t/p/w185${person.profile_path}`}
                                alt={person.name}
                                className="w-16 h-16 rounded-full object-cover mx-auto border-2 border-matrix-green/30"
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-full bg-matrix-green/10 mx-auto flex items-center justify-center">
                                <span className="text-matrix-green text-xl">{person.name[0]}</span>
                              </div>
                            )}
                            <p className="text-xs text-white mt-2 line-clamp-1">{person.name}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{person.character}</p>
                          </div>
                        ))}
                      </div>
                      <ScrollBar orientation="horizontal" />
                    </ScrollArea>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Header Component
const Header = ({ onSearch, searchQuery, setSearchQuery, region, setRegion, language, setLanguage, t }) => {
  const navigate = useNavigate();
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch(searchQuery);
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  return (
    <header className="sticky top-0 z-50 bg-matrix-dark/95 backdrop-blur-sm border-b border-matrix-green/20">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Standalone Logo + Navigation */}
          <div className="flex items-center gap-4">
            <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_matrix-ecosystem/artifacts/exlbr6x6_Standalone%20Logo.png"
                alt="My Matrix Streams"
                className="h-10 w-auto"
              />
            </a>
            <nav className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-matrix-green hover:bg-matrix-green/10"
              onClick={() => navigate('/')}
              data-testid="nav-home"
            >
              {t.home}
            </Button>
            <Button 
              variant="ghost" 
              className="text-gray-300 hover:text-matrix-green hover:bg-matrix-green/10"
              onClick={() => navigate('/discover')}
              data-testid="nav-discover"
            >
              {t.discover}
            </Button>
            <Button 
              variant="ghost" 
              className="text-gray-300 hover:text-matrix-green hover:bg-matrix-green/10"
              onClick={() => navigate('/watchlist')}
              data-testid="nav-watchlist"
            >
              <Heart className="w-4 h-4 mr-2" />
              {t.watchlist}
            </Button>
            </nav>
          </div>
          
          {/* Region and Language Selectors */}
          <div className="flex items-center gap-3">
            <Select value={region} onValueChange={setRegion}>
              <SelectTrigger className="w-20 bg-black/40 border-matrix-green/30 text-white">
                <Globe className="w-4 h-4 mr-1" />
                <span>{region}</span>
              </SelectTrigger>
              <SelectContent className="bg-matrix-dark border-matrix-green/30">
                <SelectItem value="US">US</SelectItem>
                <SelectItem value="GB">UK</SelectItem>
                <SelectItem value="CA">CA</SelectItem>
                <SelectItem value="AU">AU</SelectItem>
                <SelectItem value="DE">DE</SelectItem>
                <SelectItem value="FR">FR</SelectItem>
                <SelectItem value="IN">IN</SelectItem>
              </SelectContent>
            </Select>
            {/* Language Selector */}
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-36 bg-black/40 border-matrix-green/30 text-white" data-testid="language-selector">
                <Globe className="w-4 h-4 mr-1" />
                <span>{LANGUAGES.find(l => l.code === language)?.label || 'English'}</span>
              </SelectTrigger>
              <SelectContent className="bg-matrix-dark border-matrix-green/30">
                {LANGUAGES.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-matrix-green font-mono w-6">{lang.flag}</span>
                      <span>{lang.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="text-matrix-green">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-matrix-dark border-matrix-green/30">
              <SheetHeader>
                <SheetTitle className="text-matrix-green">Menu</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-2 mt-6">
                <Button 
                  variant="ghost" 
                  className="justify-start text-gray-300 hover:text-matrix-green"
                  onClick={() => navigate('/')}
                >
                  {t.home}
                </Button>
                <Button 
                  variant="ghost" 
                  className="justify-start text-gray-300 hover:text-matrix-green"
                  onClick={() => navigate('/discover')}
                >
                  {t.discover}
                </Button>
                <Button 
                  variant="ghost" 
                  className="justify-start text-gray-300 hover:text-matrix-green"
                  onClick={() => navigate('/watchlist')}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  {t.watchlist}
                </Button>
              </nav>
              {/* Mobile Language Selector */}
              <div className="mt-6 pt-6 border-t border-matrix-green/20">
                <p className="text-sm text-gray-400 mb-2">Language</p>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-full bg-black/40 border-matrix-green/30 text-white">
                    <Globe className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-matrix-dark border-matrix-green/30">
                    {LANGUAGES.map(lang => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-2">
                          <span className="text-xs text-matrix-green font-mono">{lang.flag}</span>
                          <span>{lang.label}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

// Footer Component
const Footer = ({ t }) => (
  <footer className="bg-black/60 border-t border-matrix-green/20 mt-12">
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="col-span-1 md:col-span-2">
          <Logo />
          <p className="text-gray-400 mt-4 max-w-md">
            {t.footerDesc}
          </p>
        </div>
        <div>
          <h4 className="text-matrix-green font-semibold mb-3">{t.ecosystem}</h4>
          <ul className="space-y-2">
            <li>
              <a href="https://moviematrixtrailers.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-matrix-green transition-colors flex items-center gap-1">
                Movie Matrix Trailers
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>
              <a href="https://matrixfilmreview.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-matrix-green transition-colors flex items-center gap-1">
                Matrix Film Review
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
            <li>
              <a href="https://www.youtube.com/@TheMovieMatrixTV" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-matrix-green transition-colors flex items-center gap-1">
                The Movie Matrix TV (YouTube)
                <ExternalLink className="w-3 h-3" />
              </a>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-matrix-green font-semibold mb-3">{t.about}</h4>
          <ul className="space-y-2">
            <li>
              <span className="text-gray-400 text-sm">{t.streamingData}</span>
            </li>
            <li>
              <span className="text-gray-400 text-sm">{t.noHost}</span>
            </li>
            <li>
              <span className="text-gray-400 text-sm">{t.officialProviders}</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-matrix-green/10 mt-8 pt-8 text-center text-gray-500 text-sm">
        © {new Date().getFullYear()} {t.copyright}
      </div>
    </div>
  </footer>
);

// Home Page
const HomePage = ({ region, onItemClick, t, searchQuery, setSearchQuery, onSearch }) => {
  const [trendingMovies, setTrendingMovies] = useState([]);
  const [trendingTV, setTrendingTV] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchTrending = async () => {
      setLoading(true);
      try {
        const [movies, tv] = await Promise.all([
          axios.get(`${API}/trending/movie/week`),
          axios.get(`${API}/trending/tv/week`)
        ]);
        setTrendingMovies(movies.data.results || []);
        setTrendingTV(tv.data.results || []);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load trending content');
      } finally {
        setLoading(false);
      }
    };
    fetchTrending();
  }, []);
  
  const handleHeroSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch(searchQuery);
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };
  
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-matrix-green/10 via-transparent to-black/50" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-matrix-green/5 via-transparent to-transparent" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo and Site Title */}
            <div className="flex flex-col items-center mb-6">
              <Logo size="large" />
              <h1 className="text-3xl md:text-5xl font-bold text-matrix-green mt-4 tracking-wider">
                MY MATRIX STREAMS
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mt-3">
                {t.heroSubtitle}
              </p>
            </div>
            
            {/* Hero Search Bar */}
            <form onSubmit={handleHeroSearch} className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  data-testid="hero-search-input"
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-12 pr-4 py-6 text-lg bg-black/60 border-matrix-green/40 text-white placeholder:text-gray-500 focus:border-matrix-green rounded-full"
                />
                <Button 
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-matrix-green text-black hover:bg-matrix-green/90 rounded-full px-6"
                >
                  <Search className="w-4 h-4 mr-2" />
                  {t.home === 'Inicio' ? 'Buscar' : t.home === 'Accueil' ? 'Rechercher' : t.home === 'Startseite' ? 'Suchen' : 'Search'}
                </Button>
              </div>
            </form>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Badge variant="outline" className="border-matrix-green/40 text-matrix-green px-4 py-2">
                <Film className="w-4 h-4 mr-2" />
                {t.streamingServices}
              </Badge>
              <Badge variant="outline" className="border-matrix-green/40 text-matrix-green px-4 py-2">
                <Globe className="w-4 h-4 mr-2" />
                {t.regionAware}
              </Badge>
              <Badge variant="outline" className="border-matrix-green/40 text-matrix-green px-4 py-2">
                <Heart className="w-4 h-4 mr-2" />
                {t.personalWatchlist}
              </Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Content Rows */}
      <section className="container mx-auto px-4 py-8">
        <ContentRow 
          title={t.trendingMovies} 
          items={trendingMovies} 
          loading={loading}
          onItemClick={onItemClick}
          icon={TrendingUp}
        />
        <ContentRow 
          title={t.trendingTV} 
          items={trendingTV} 
          loading={loading}
          onItemClick={onItemClick}
          icon={TrendingUp}
        />
      </section>
    </div>
  );
};

// Search Results Page
const SearchPage = ({ region, onItemClick, t }) => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mediaFilter, setMediaFilter] = useState('all');
  
  useEffect(() => {
    if (query) {
      setLoading(true);
      setPage(1);
      axios.get(`${API}/search/multi`, { params: { query, region, page: 1 } })
        .then(res => {
          setResults(res.data.results || []);
          setTotalPages(res.data.total_pages || 1);
        })
        .catch(err => {
          console.error(err);
          toast.error('Search failed');
        })
        .finally(() => setLoading(false));
    }
  }, [query, region]);
  
  const loadMore = async () => {
    if (page >= totalPages) return;
    const nextPage = page + 1;
    try {
      const res = await axios.get(`${API}/search/multi`, { params: { query, region, page: nextPage } });
      setResults(prev => [...prev, ...(res.data.results || [])]);
      setPage(nextPage);
    } catch (err) {
      console.error(err);
    }
  };
  
  const filteredResults = mediaFilter === 'all' 
    ? results 
    : results.filter(r => r.media_type === mediaFilter);
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">
          {t.searchResults} "{query}"
        </h1>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-matrix-green" />
          <Select value={mediaFilter} onValueChange={setMediaFilter}>
            <SelectTrigger className="w-32 bg-black/40 border-matrix-green/30 text-white">
              <span className="text-white">
                {mediaFilter === 'all' ? t.all : mediaFilter === 'movie' ? t.movies : t.tvShows}
              </span>
            </SelectTrigger>
            <SelectContent className="bg-matrix-dark border-matrix-green/30">
              <SelectItem value="all">{t.all}</SelectItem>
              <SelectItem value="movie">{t.movies}</SelectItem>
              <SelectItem value="tv">{t.tvShows}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i}>
              <Skeleton className="aspect-[2/3] bg-matrix-green/10" />
              <Skeleton className="h-4 mt-2 bg-matrix-green/10" />
            </div>
          ))}
        </div>
      ) : filteredResults.length > 0 ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {filteredResults.map((item) => (
              <ContentCard 
                key={`${item.media_type}-${item.id}`} 
                item={item} 
                onClick={onItemClick}
              />
            ))}
          </div>
          {page < totalPages && (
            <div className="text-center mt-8">
              <Button 
                variant="outline" 
                className="border-matrix-green text-matrix-green hover:bg-matrix-green/10"
                onClick={loadMore}
              >
                {t.loadMore}
              </Button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <Search className="w-16 h-16 text-matrix-green/30 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">{t.noResults} "{query}"</p>
          <p className="text-gray-500 mt-2">{t.tryDifferent}</p>
        </div>
      )}
    </div>
  );
};

// Discover Page
const DiscoverPage = ({ region, onItemClick, t }) => {
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mediaType, setMediaType] = useState('movie');
  const [sortBy, setSortBy] = useState('popularity.desc');
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Fetch genres
  useEffect(() => {
    axios.get(`${API}/genres/${mediaType}`)
      .then(res => setGenres(res.data.genres || []))
      .catch(console.error);
  }, [mediaType]);
  
  // Fetch content
  useEffect(() => {
    setLoading(true);
    setPage(1);
    const params = {
      page: 1,
      sort_by: sortBy,
      watch_region: region
    };
    if (selectedGenre) params.with_genres = selectedGenre;
    
    axios.get(`${API}/discover/${mediaType}`, { params })
      .then(res => {
        setContent(res.data.results || []);
        setTotalPages(res.data.total_pages || 1);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load content');
      })
      .finally(() => setLoading(false));
  }, [mediaType, sortBy, selectedGenre, region]);
  
  const loadMore = async () => {
    if (page >= totalPages) return;
    const nextPage = page + 1;
    const params = {
      page: nextPage,
      sort_by: sortBy,
      watch_region: region
    };
    if (selectedGenre) params.with_genres = selectedGenre;
    
    try {
      const res = await axios.get(`${API}/discover/${mediaType}`, { params });
      setContent(prev => [...prev, ...(res.data.results || [])]);
      setPage(nextPage);
    } catch (err) {
      console.error(err);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-white mb-6">{t.discover}</h1>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-8">
        <Tabs value={mediaType} onValueChange={(v) => { setMediaType(v); setSelectedGenre(''); }}>
          <TabsList className="bg-black/40">
            <TabsTrigger value="movie" className="data-[state=active]:bg-matrix-green data-[state=active]:text-black">
              <Film className="w-4 h-4 mr-2" />
              {t.movies}
            </TabsTrigger>
            <TabsTrigger value="tv" className="data-[state=active]:bg-matrix-green data-[state=active]:text-black">
              <Tv className="w-4 h-4 mr-2" />
              {t.tvShows}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-48 bg-black/40 border-matrix-green/30 text-white">
            <span className="text-white">
              {sortBy === 'popularity.desc' ? t.mostPopular : 
               sortBy === 'vote_average.desc' ? t.topRated :
               sortBy === 'primary_release_date.desc' ? t.newest : t.highestRevenue}
            </span>
          </SelectTrigger>
          <SelectContent className="bg-matrix-dark border-matrix-green/30">
            <SelectItem value="popularity.desc">{t.mostPopular}</SelectItem>
            <SelectItem value="vote_average.desc">{t.topRated}</SelectItem>
            <SelectItem value="primary_release_date.desc">{t.newest}</SelectItem>
            <SelectItem value="revenue.desc">{t.highestRevenue}</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={selectedGenre || "all"} onValueChange={(v) => setSelectedGenre(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44 bg-black/40 border-matrix-green/30 text-white">
            <span className="text-white">
              {selectedGenre ? (genres.find(g => g.id.toString() === selectedGenre)?.name || t.allGenres) : t.allGenres}
            </span>
          </SelectTrigger>
          <SelectContent className="bg-matrix-dark border-matrix-green/30 max-h-60">
            <SelectItem value="all">{t.allGenres}</SelectItem>
            {genres.map(genre => (
              <SelectItem key={genre.id} value={genre.id.toString()}>{genre.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      {/* Content Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {[...Array(18)].map((_, i) => (
            <div key={i}>
              <Skeleton className="aspect-[2/3] bg-matrix-green/10" />
              <Skeleton className="h-4 mt-2 bg-matrix-green/10" />
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
            {content.map((item) => (
              <ContentCard 
                key={`${item.media_type}-${item.id}`} 
                item={item} 
                onClick={onItemClick}
              />
            ))}
          </div>
          {page < totalPages && (
            <div className="text-center mt-8">
              <Button 
                variant="outline" 
                className="border-matrix-green text-matrix-green hover:bg-matrix-green/10"
                onClick={loadMore}
              >
                {t.loadMore}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Watchlist Page
const WatchlistPage = ({ onItemClick, t }) => {
  const [watchlist, setWatchlist] = useState([]);
  
  useEffect(() => {
    setWatchlist(getWatchlist());
  }, []);
  
  const handleRemove = (id, media_type) => {
    removeFromWatchlist(id, media_type);
    setWatchlist(getWatchlist());
    toast.success(t.removedFromWatchlist);
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Heart className="w-8 h-8 text-matrix-green fill-matrix-green" />
        <h1 className="text-3xl font-bold text-white">{t.myWatchlist}</h1>
      </div>
      
      {watchlist.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {watchlist.map((item) => (
            <Card 
              key={`${item.media_type}-${item.id}`}
              className="group cursor-pointer bg-black/40 border-matrix-green/20 hover:border-matrix-green/60 transition-all duration-300 overflow-hidden"
            >
              <div className="relative aspect-[2/3] overflow-hidden" onClick={() => onItemClick(item)}>
                {item.poster_url ? (
                  <img 
                    src={item.poster_url} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full bg-matrix-dark flex items-center justify-center">
                    <Film className="w-12 h-12 text-matrix-green/30" />
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 text-red-500 bg-black/60 hover:bg-black/80"
                  onClick={(e) => { e.stopPropagation(); handleRemove(item.id, item.media_type); }}
                >
                  <X className="w-5 h-5" />
                </Button>
                <Badge className="absolute top-2 left-2 bg-matrix-green/90 text-black text-xs">
                  {item.media_type === 'movie' ? t.movies.slice(0, -1) : 'TV'}
                </Badge>
              </div>
              <CardContent className="p-3">
                <h3 className="font-semibold text-white text-sm line-clamp-2">{item.title}</h3>
                {item.release_date && (
                  <p className="text-matrix-green/60 text-xs mt-1">
                    {new Date(item.release_date).getFullYear()}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <Heart className="w-16 h-16 text-matrix-green/30 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">{t.emptyWatchlist}</p>
          <p className="text-gray-500 mt-2">{t.startAdding}</p>
        </div>
      )}
    </div>
  );
};

// Main App Component
function App() {
  const [searchQuery, setSearchQuery] = useState('');
  const [region, setRegion] = useState('US');
  const [language, setLanguage] = useState(() => {
    // Try to get saved language or detect from browser
    const saved = localStorage.getItem('mymatrix_language');
    if (saved && TRANSLATIONS[saved]) return saved;
    const browserLang = navigator.language.split('-')[0];
    return TRANSLATIONS[browserLang] ? browserLang : 'en';
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  
  // Save language preference
  useEffect(() => {
    localStorage.setItem('mymatrix_language', language);
  }, [language]);
  
  const t = useTranslation(language);
  
  const handleItemClick = (item) => {
    setSelectedItem(item);
    setDetailModalOpen(true);
  };
  
  const handleSearch = (query) => {
    setSearchQuery(query);
  };
  
  return (
    <div className="App min-h-screen bg-matrix-dark">
      <BrowserRouter>
        <Header 
          onSearch={handleSearch}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          region={region}
          setRegion={setRegion}
          language={language}
          setLanguage={setLanguage}
          t={t}
        />
        
        <main className="min-h-[calc(100vh-200px)]">
          <Routes>
            <Route path="/" element={
              <HomePage 
                region={region} 
                onItemClick={handleItemClick} 
                t={t} 
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onSearch={handleSearch}
              />
            } />
            <Route path="/search" element={<SearchPage region={region} onItemClick={handleItemClick} t={t} />} />
            <Route path="/discover" element={<DiscoverPage region={region} onItemClick={handleItemClick} t={t} />} />
            <Route path="/watchlist" element={<WatchlistPage onItemClick={handleItemClick} t={t} />} />
          </Routes>
        </main>
        
        <Footer t={t} />
        
        <ContentDetailModal 
          item={selectedItem}
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          t={t}
        />
        
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
